import React from 'react';
import clsx from 'clsx';
import { Package, ShoppingCart, Tag, BarChart3 } from 'lucide-react';
import { useVocab, useIsAppointments } from '../../../hooks/useVocab';

/**
 * One metric tile + the grid of four tiles. Keeping these inline (one file)
 * because they're tightly coupled — the row is conceptually a single unit.
 */
function MetricCard({ Icon, label, value, hint, tone }) {
    const tones = {
        brand:   { chip: 'bg-brand-100 text-brand-700',     value: 'text-ink-strong', bar: 'bg-brand-500' },
        success: { chip: 'bg-success-100 text-success-700', value: 'text-ink-strong', bar: 'bg-success-500' },
        info:    { chip: 'bg-info-100 text-info-700',       value: 'text-ink-strong', bar: 'bg-info-500' },
        warning: { chip: 'bg-warning-100 text-warning-700', value: 'text-ink-strong', bar: 'bg-warning-500' },
    }[tone] || { chip: 'bg-surface-muted text-ink-muted', value: 'text-ink-strong', bar: 'bg-line-strong' };

    return (
        <div className="group relative overflow-hidden bg-surface rounded-2xl p-4 sm:p-5 border border-line shadow-xs hover:shadow-card hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200">
            <span className={clsx('absolute inset-x-0 top-0 h-1', tones.bar)} aria-hidden="true" />
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-xs font-semibold text-ink-muted tracking-wide flex-1 min-w-0">{label}</div>
                <div className={clsx('inline-flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-xl shrink-0 transition-transform duration-200 group-hover:scale-105', tones.chip)}>
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
            </div>
            <div className={clsx('text-2xl sm:text-3xl font-bold tabular-nums whitespace-nowrap overflow-hidden leading-tight', tones.value)}>{value}</div>
            <div className="text-xs text-ink-subtle mt-1">{hint}</div>
        </div>
    );
}

export function DashboardMetrics({ products, activeOrders, categories, totalIncome, ordersLoading }) {
    const t = useVocab();
    const isAppt = useIsAppointments();
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
                Icon={Package}
                tone="brand"
                label={t.items}
                value={products?.length || 0}
                hint={isAppt ? 'שירותים פעילים' : 'סה"כ פריטים פעילים'}
            />
            <MetricCard
                Icon={ShoppingCart}
                tone="success"
                label={isAppt ? 'תורים פעילים' : 'הזמנות פעילות'}
                value={ordersLoading ? '...' : activeOrders.length}
                hint="ממתינות לטיפול"
            />
            <MetricCard
                Icon={Tag}
                tone="info"
                label="קטגוריות"
                value={categories?.length || 0}
                hint="לארגון יעיל יותר"
            />
            <MetricCard
                Icon={BarChart3}
                tone="warning"
                label="הכנסות"
                value={ordersLoading ? '...' : `₪${totalIncome.toLocaleString('he-IL', { maximumFractionDigits: 0 })}`}
                hint='סה"כ ללא ארכיון'
            />
        </div>
    );
}

export default DashboardMetrics;
